/** 信号收件箱（inbox.md）：投递结果、信号线节奏、按线按日索引、收件明细、最近桥接动作、触发桥接。
 *
 * 收件明细以后端统一形状为准：拒收 / 不下单条目只认 `ref`（裁决 B9），`delivery` 可为 null。
 */

import { useState } from "react";

import { useAction } from "../../api/mutations";
import { inboxEventLabel, ingestBody } from "../../lib/inboxIngest";

import { useBridgeEvents, useInboxDetail, useInboxIndex, useMirror, freshnessText, inboxEmptyNotes } from "../../api/g4";
import { useSignals } from "../../api/queries";
import { bridgeOutcomeView } from "../../lib/g1";
import { useDailyChain } from "../../lib/g1Queries";
import { Card, Kpi, Kv, Lede, Note, PageHead } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { Table } from "../../components/Table";
import { ts } from "../../lib/format";
import { Missing, Section } from "../g4/Section";
import { ActionForm, RawDetails } from "../p2/Common";

export function InboxScreen(): React.ReactNode {
  const signals = useSignals();
  const index = useInboxIndex();
  const mirror = useMirror();
  const events = useBridgeEvents();
  const [pick, setPick] = useState<{ strategy: string; date: string } | null>(null);
  const detail = useInboxDetail(pick?.strategy ?? "", pick?.date ?? "");
  const latestDate = index.data && index.data.length ? index.data.map((row) => row.date).sort().at(-1) ?? "" : "";
  const chain = useDailyChain(latestDate || undefined);
  // 结论只取 /chain/daily 桥接一步的 per_line（服务端判定）；只有链路所查那一天的行能对上，其余行标「缺」。
  const bridgeStep = chain.status === "success" ? chain.data.steps.find((step) => step.step === "bridge") ?? null : null;
  const lineOutcome = (line: string | null, date: string) => {
    if (!bridgeStep || !line || chain.data?.trade_date !== date) return null;
    return bridgeStep.per_line.find((item) => item.source === line) ?? null;
  };

  return (
    <>
      <PageHead title="信号收件箱" sub="GET /signals · /signals/inbox · /paper/events" />
      <Lede>两条信号线每天交进来什么、哪条是上游无信号哪条是桥接没跑、哪条超期没有新信号。收件明细按后端统一形状显示，拒收与不下单条目只列 ref。</Lede>

      <Card title="投递结果（常驻台账）" sub="GET /signals · 只统计 POST /signals/deliver">
        <Section query={signals} what="常驻信号台账" path="/signals">
          {(data) =>
            data.accepted.length + data.duplicates.length + data.rejected.length === 0 ? (
              <div className="empty">常驻台账还没有投递记录（只统计 POST /signals/deliver，纸上盘收件见下方按线按日）</div>
            ) : (
              <div className="grid g3">
                <Kpi label="受理" value={data.accepted.length} />
                <Kpi label="重复（只合并计次）" value={data.duplicates.length} />
                <Kpi label="拒收" value={<a href="#rejected">{data.rejected.length}</a>} />
              </div>
            )
          }
        </Section>
      </Card>

      <Card title="信号线节奏" sub="GET /data/mirror · lines[]（someopark 1 天 / someopark-test 3 天）">
        <Section query={mirror} what="来源节奏" path="/data/mirror">
          {(data) => (
            <Table
              cols={[{ label: "信号线" }, { label: "取数依据" }, { label: "最近数据日" }, { label: "约定周期", num: true }, { label: "结论", wrap: true }]}
              rows={data.lines.map((line) => {
                const f = freshnessText(line);
                return {
                  key: line.source_line,
                  cls: line.stale ? "bad" : "",
                  cells: [
                    line.source_line,
                    line.basis,
                    line.latest_day ?? "—",
                    `${line.cadence_days ?? "—"} 天`,
                    line.latest_day === null ? "这条线还没有产出过信号" : <Pill tone={f.tone === "bad" ? "bad" : f.tone === "ok" ? "ok" : "neutral"}>{line.stale ? `疑似停产：${f.text}` : f.text}</Pill>,
                  ],
                };
              })}
            />
          )}
        </Section>
      </Card>

      <Card title="按线按日" sub="GET /signals/inbox · 点行看收件明细" nopad>
        <Section query={index} what="收件索引" path="/signals/inbox">
          {(rows) => (
            <Table
              cols={[{ label: "信号线" }, { label: "策略", mono: true }, { label: "日期" }, { label: "受理", num: true }, { label: "拒收", num: true }, { label: "不下单", num: true }, { label: "输入条数", num: true }, { label: "文件形状" }, { label: "结论" }]}
              rows={rows.map((row) => ({
                key: `${row.strategy_id}/${row.date}`,
                selected: pick?.strategy === row.strategy_id && pick?.date === row.date,
                onClick: () => setPick({ strategy: row.strategy_id, date: row.date }),
                cells: [
                  row.source_line ?? <Missing />,
                  row.strategy_id,
                  row.date,
                  row.accepted,
                  row.rejected ? <a href="#rejected">{row.rejected}</a> : 0,
                  row.no_order,
                  row.input_count ?? <Missing />,
                  row.shape === "ingest" ? "paper ingest" : "桥接报告",
                  // 结论来自 /chain/daily；它未提供或不是所查那天时标「缺」，不拿「有无文件」自行推断。
                  (() => {
                    const hit = lineOutcome(row.source_line, row.date);
                    if (!hit) return <Missing />;
                    const v = bridgeOutcomeView(hit.outcome);
                    return <Pill tone={v.tone}>{v.text}</Pill>;
                  })(),
                ],
              }))}
              empty="还没有任何收件（var/paper/<sid>/inbox/ 为空），尚未桥接过"
            />
          )}
        </Section>
        <div className="body">
          <Section query={chain} what="链路结论" path="/chain/daily" missing="按线按日只显示索引计数，结论列标「缺」">
            {(data) => <Note>结论列取 {data.trade_date} 链路「桥接」一步的按线结论；其它日期的行标「缺」。</Note>}
          </Section>
        </div>
      </Card>

      <Card title="收件明细" sub={pick ? `${pick.strategy} / ${pick.date}` : "在上表选一行"}>
        {pick ? (
          <Section query={detail} what="收件明细" path={`/signals/inbox?strategy=${pick.strategy}&date=${pick.date}`}>
            {(data) => <InboxDetail data={data} />}
          </Section>
        ) : (
          <div className="empty">在「按线按日」选一行</div>
        )}
      </Card>

      <div className="grid g2" style={{ marginTop: 12 }}>
        <Card title="最近收件动作" sub="GET /paper/events · kind=bridge / inbox_ingest" nopad>
          <Section query={events} what="收件动作" path="/paper/events">
            {(rows) => (
              <Table
                cols={[{ label: "时间" }, { label: "来源" }, { label: "交易日" }, { label: "结论" }, { label: "写出策略", wrap: true }, { label: "触发人" }, { label: "理由", wrap: true }]}
                rows={rows.map((row, i) => ({
                  key: `${row.ts_ns}-${i}`,
                  cells: [
                    ts(row.ts_ns),
                    row.kind === "inbox_ingest" ? "手工" : row.source,
                    row.trade_date ?? <Missing />,
                    (() => {
                      const v = inboxEventLabel(row.kind, row.outcome);
                      return v.tone === "missing" ? <Missing /> : <Pill tone={v.tone}>{v.text}</Pill>;
                    })(),
                    row.strategies === null ? <Missing /> : row.strategies.join("、") || "—",
                    row.operator,
                    row.reason,
                  ],
                }))}
                empty="还没有人工或定时触发过桥接，也没有手工落过收件"
              />
            )}
          </Section>
        </Card>
        <ActionForm
          path="/signals/bridge"
          title="触发桥接（将覆盖该线该日已有收件）"
          fields={[
            { name: "source", label: "source", type: "select", options: ["someopark", "someopark-test"] },
            { name: "trade_date", label: "trade_date（someopark 必填）", type: "date", required: false },
            { name: "file", label: "file（someopark-test 填 combined 文件路径；不填须勾下项并给 trade_date）", required: false },
            { name: "fill_empty", label: "上游无记录也补 0 条产物（fill_empty）", type: "checkbox", required: false },
            { name: "operator", label: "operator" },
            { name: "reason", label: "reason" },
          ]}
        />
      </div>
      <ManualIngestForm strategies={index.data ? [...new Set(index.data.map((row) => row.strategy_id))].sort() : []} />
    </>
  );
}

/** 手工落收件（#5010）：选策略 + 交易日 + 上传桥接文件，走 `POST /signals/inbox/ingest`（与 paper ingest 同一入口）。 */
function ManualIngestForm({ strategies }: { strategies: string[] }): React.ReactNode {
  const action = useAction("/signals/inbox/ingest");
  const [v, setV] = useState({ strategy_id: "", trade_date: "", overwrite: false, operator: "", reason: "" });
  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const built = ingestBody({ ...v, fileText: file?.text ?? null });
  return (
    <Card title="手工落收件" sub="POST /signals/inbox/ingest · 只写收件不下单，停机时不拒">
      <div className="fieldrow">
        <label className="f">
          <span>策略（须已登记）</span>
          <input list="inbox-strategies" value={v.strategy_id} onChange={(e) => setV({ ...v, strategy_id: e.target.value })} />
          <datalist id="inbox-strategies">{strategies.map((sid) => <option key={sid} value={sid} />)}</datalist>
        </label>
        <label className="f"><span>交易日</span><input type="date" value={v.trade_date} onChange={(e) => setV({ ...v, trade_date: e.target.value })} /></label>
        <label className="f">
          <span>桥接文件（JSON）{file ? `：${file.name}` : ""}</span>
          <input
            type="file"
            accept=".json,application/json"
            onChange={(e) => {
              const picked = e.target.files?.[0];
              if (!picked) return setFile(null);
              void picked.text().then((text) => setFile({ name: picked.name, text }));
            }}
          />
        </label>
      </div>
      <div className="fieldrow">
        <label className="f check"><span>覆盖已有收件（overwrite）</span><input type="checkbox" checked={v.overwrite} onChange={(e) => setV({ ...v, overwrite: e.target.checked })} /></label>
        <label className="f"><span>operator</span><input value={v.operator} onChange={(e) => setV({ ...v, operator: e.target.value })} /></label>
        <label className="f"><span>reason</span><input value={v.reason} onChange={(e) => setV({ ...v, reason: e.target.value })} /></label>
      </div>
      <button className="btn primary" disabled={!built.ok || action.isPending} onClick={() => built.ok && action.mutate(built.body)}>
        {action.isPending ? "提交中…" : "落收件"}
      </button>
      {!built.ok ? <Note tone="warn">{built.error}</Note> : null}
      {action.isError ? <Note tone="bad">{action.error instanceof Error ? action.error.message : String(action.error)}</Note> : null}
      {action.isSuccess ? <RawDetails value={action.data as object} /> : null}
    </Card>
  );
}

function InboxDetail({ data }: { data: NonNullable<ReturnType<typeof useInboxDetail>["data"]> }): React.ReactNode {
  const notes = inboxEmptyNotes(data);
  return (
    <>
      <Kv
        pairs={[
          ["来源", data.source ?? "—"],
          ["交易日", data.trade_date],
          ["输入条数", data.input_count ?? "缺"],
          ["文件形状", data.shape === "ingest" ? "paper ingest（{signals, bridge}）" : "桥接报告"],
          ["触发人", data.operator ?? "—"],
          ["理由", data.reason ?? "—"],
          ["零产物", data.empty_reason === null && data.upstream_latest_date === null ? "否" : `是：${data.empty_reason ?? "原因未写"}（上游最新 ${data.upstream_latest_date ?? "未知"}）`],
          ["投递", data.delivery === null ? "尚未被纸上盘消费" : JSON.stringify(data.delivery)],
        ]}
      />
      {notes.length ? <Note>{notes.join("；")}</Note> : null}
      <Table
        cols={[{ label: "signal_id", mono: true }, { label: "标的" }, { label: "方向" }, { label: "目标权重", num: true }, { label: "有效期" }, { label: "研究流程" }, { label: "版本" }, { label: "参数", mono: true }, { label: "数据快照", mono: true }, { label: "合约" }]}
        rows={data.signals.map((s) => ({
          key: s.signal_id,
          cells: [s.signal_id, s.instrument_id, s.direction, s.target_weight, `${ts(s.valid_from)} ~ ${ts(s.valid_until)}`, s.source.research_flow, s.source.version, s.source.params_digest, s.source.data_snapshot, `v${s.contract_version}`],
        }))}
        empty="这份收件没有受理的信号"
      />
      <div className="grid g2" style={{ marginTop: 12 }}>
        <RefTable title="拒收" rows={data.rejected} empty="无拒收" />
        <RefTable title="不下单" rows={data.no_order} empty="无不下单条目" />
      </div>
      {data.inputs ? <RawDetails value={data.inputs} /> : <Note tone="warn">收件没有 inputs（输入指纹缺）</Note>}
    </>
  );
}

function RefTable({ title, rows, empty }: { title: string; rows: { ref: string | null; ref_kind: string | null; reason: string | null }[]; empty: string }): React.ReactNode {
  return (
    <Card title={title} sub={`${rows.length} 条`} nopad>
      <Table
        cols={[{ label: "ref", mono: true }, { label: "原键" }, { label: "原因", wrap: true }]}
        rows={rows.map((row, i) => ({ key: `${row.ref}-${i}`, cells: [row.ref ?? <Missing />, row.ref_kind ?? "—", row.reason ?? <Missing />] }))}
        empty={empty}
      />
    </Card>
  );
}
